import { FeeEngineError, assertValidRule } from "@netkeep/fee-engine";
import {
  BASIS_COMPONENTS,
  FEE_CATEGORIES,
  SCHEDULE_STATUSES,
  TAX_COLLECTORS,
  TRANSACTION_TYPES,
  VERIFICATION_STATUSES,
  type CalculationInput,
  type FeeRule,
} from "@netkeep/fee-types";
import { z } from "zod";

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

const DECIMAL_RE = /^\d{1,13}(\.\d{1,6})?$/;

/**
 * Non-negative decimal. Accepts a JSON number or string and always yields a
 * string so downstream code never touches binary floats. Numbers are
 * accepted for client convenience but strings are recommended.
 */
export const decimalString = z
  .union([z.string(), z.number()])
  .transform((v) => (typeof v === "number" ? String(v) : v.trim()))
  .pipe(z.string().regex(DECIMAL_RE, "Must be a non-negative decimal with at most 6 decimal places"));

/** Percentage between 0 and 100 inclusive. */
export const percentString = decimalString.refine((v) => Number(v) <= 100, "Must be between 0 and 100");

export const currencyCode = z
  .string()
  .regex(/^[A-Za-z]{3}$/, "Must be a 3-letter ISO 4217 code")
  .transform((v) => v.toUpperCase());

export const regionCode = z
  .string()
  .regex(/^[A-Za-z]{2}$/, "Must be a 2-letter ISO 3166-1 code")
  .transform((v) => v.toUpperCase());

export const slug = z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Must be a lowercase slug").max(64);

export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD");

export const uuidParam = z.object({ id: z.uuid() });

export const basisComponent = z.enum(BASIS_COMPONENTS);
export const feeCategory = z.enum(FEE_CATEGORIES);
export const taxCollector = z.enum(TAX_COLLECTORS);
export const transactionType = z.enum(TRANSACTION_TYPES);
export const verificationStatus = z.enum(VERIFICATION_STATUSES);
export const scheduleStatus = z.enum(SCHEDULE_STATUSES);

const basisList = z.array(basisComponent).min(1).max(BASIS_COMPONENTS.length);

// ---------------------------------------------------------------------------
// Fee rules & profiles
// ---------------------------------------------------------------------------

export const feeRuleSchema = z
  .object({
    code: z.string().regex(/^[A-Z][A-Z0-9_]{0,47}$/, "Must be UPPER_SNAKE_CASE"),
    label: z.string().min(1).max(120),
    category: feeCategory,
    basis: basisList,
    percent: percentString.optional(),
    percentTiers: z
      .array(z.object({ upTo: decimalString.nullable(), percent: percentString }))
      .min(1)
      .max(20)
      .optional(),
    fixedAmount: decimalString.optional(),
    fixedTiers: z
      .array(z.object({ upTo: decimalString.nullable(), amount: decimalString }))
      .min(1)
      .max(20)
      .optional(),
    minimumFee: decimalString.optional(),
    maximumFee: decimalString.optional(),
  })
  .superRefine((rule, ctx) => {
    // Reuse the engine's structural checks so API and engine never disagree.
    try {
      assertValidRule(rule as FeeRule);
    } catch (err) {
      if (err instanceof FeeEngineError) ctx.addIssue({ code: "custom", message: err.message });
      else throw err;
    }
  });

function uniqueRuleCodes(rules: { code: string }[], ctx: z.RefinementCtx) {
  const seen = new Set<string>();
  for (const rule of rules) {
    if (seen.has(rule.code)) ctx.addIssue({ code: "custom", message: `Duplicate rule code ${rule.code}`, path: ["rules"] });
    seen.add(rule.code);
  }
}

export const feeProfileSchema = z
  .object({
    id: z.string().min(1).max(100),
    name: z.string().min(1).max(120),
    currency: currencyCode,
    taxCollectedBy: taxCollector,
    paymentProcessingIncluded: z.boolean(),
    promotedListingBasis: basisList,
    rules: z.array(feeRuleSchema).max(30),
  })
  .superRefine((p, ctx) => uniqueRuleCodes(p.rules, ctx));

// ---------------------------------------------------------------------------
// Calculation input
// ---------------------------------------------------------------------------

export const salesTaxSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("NONE") }),
  z.object({ mode: z.literal("RATE"), ratePercent: percentString, includeShipping: z.boolean().default(true) }),
  z.object({ mode: z.literal("AMOUNT"), amount: decimalString }),
]);

const manualOverride = z.object({
  percent: percentString.optional(),
  fixedAmount: decimalString.optional(),
  basis: basisList.optional(),
});

const money = decimalString.default("0");

export const calculationInputSchema = z.object({
  currency: currencyCode.default("USD"),
  salePrice: decimalString,
  shippingCharged: money,
  salesTax: salesTaxSchema.default({ mode: "NONE" }),
  itemCost: money,
  shippingCost: money,
  packagingCost: money,
  otherExpenses: money,
  subscriptionOverhead: money,
  promotedListingPercent: percentString.optional(),
  taxCollectedBy: taxCollector.optional(),
  overrides: z
    .object({
      marketplace: manualOverride.optional(),
      paymentProcessing: manualOverride.optional(),
    })
    .optional(),
}) satisfies z.ZodType<CalculationInput, unknown>;

/** Body of POST /api/v1/calculations: inputs plus a schedule id, inline profile, or neither (custom). */
export const calculationRequestSchema = z
  .object({
    input: calculationInputSchema,
    feeScheduleId: z.uuid().optional(),
    profile: feeProfileSchema.optional(),
    targetProfit: z
      .string()
      .regex(/^-?\d{1,13}(\.\d{1,2})?$/)
      .optional(),
  })
  .refine((b) => !(b.feeScheduleId && b.profile), {
    message: "Provide either feeScheduleId or profile, not both",
    path: ["profile"],
  });

// ---------------------------------------------------------------------------
// Fee schedules (admin)
// ---------------------------------------------------------------------------

export const feeScheduleCreateSchema = z
  .object({
    name: z.string().min(1).max(200),
    platformSlug: slug,
    paymentMethodSlug: slug,
    region: regionCode,
    currency: currencyCode,
    transactionType: transactionType,
    sellerTier: z.string().min(1).max(64).default("standard"),
    itemCategory: z.string().min(1).max(120).default("all"),
    versionLabel: z.string().min(1).max(64),
    effectiveFrom: isoDate,
    effectiveTo: isoDate.nullable().default(null),
    taxCollectedBy: taxCollector,
    paymentProcessingIncluded: z.boolean(),
    promotedListingBasis: basisList,
    rules: z.array(feeRuleSchema).max(30),
    source: z.object({ url: z.url(), title: z.string().min(1).max(200) }),
    notes: z.string().max(2000).nullable().default(null),
  })
  .superRefine((s, ctx) => {
    uniqueRuleCodes(s.rules, ctx);
    if (s.effectiveTo && s.effectiveTo <= s.effectiveFrom) {
      ctx.addIssue({ code: "custom", message: "effectiveTo must be after effectiveFrom", path: ["effectiveTo"] });
    }
  });

export type FeeScheduleCreate = z.infer<typeof feeScheduleCreateSchema>;

export const feeScheduleQuerySchema = z.object({
  platform: slug.optional(),
  paymentMethod: slug.optional(),
  region: regionCode.optional(),
  transactionType: transactionType.optional(),
  status: scheduleStatus.optional(),
  /** ISO date at which the schedule must be in effect. */
  asOf: isoDate.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const scheduleReviewSchema = z.object({
  /** Who verified the schedule against its official source. */
  verifiedBy: z.string().min(1).max(120),
  /** Required when the new version differs sharply from the active one. */
  acknowledgeLargeChanges: z.boolean().default(false),
  notes: z.string().max(2000).optional(),
});

export const scheduleRejectSchema = z.object({
  rejectedBy: z.string().min(1).max(120),
  reason: z.string().min(1).max(2000),
});

// ---------------------------------------------------------------------------
// Mobile calculator form
// ---------------------------------------------------------------------------

/** Text-field money: empty means zero. Commas and "$" are tolerated for typing comfort. */
const formMoney = z
  .string()
  .transform((v) => v.replace(/[$,\s]/g, ""))
  .refine((v) => v === "" || DECIMAL_RE.test(v), "Enter a valid amount")
  .transform((v) => (v === "" ? "0" : v));

const formPercent = formMoney.refine((v) => Number(v) <= 100, "Must be 100% or less");

const formOptionalPercent = z
  .string()
  .transform((v) => v.replace(/[%\s]/g, ""))
  .refine((v) => v === "" || (DECIMAL_RE.test(v) && Number(v) <= 100), "Enter a percent from 0 to 100")
  .transform((v) => (v === "" ? undefined : v));

const formOptionalMoney = z
  .string()
  .transform((v) => v.replace(/[$,\s]/g, ""))
  .refine((v) => v === "" || DECIMAL_RE.test(v), "Enter a valid amount")
  .transform((v) => (v === "" ? undefined : v));

export const calculatorFormSchema = z.object({
  salePrice: formMoney,
  shippingCharged: formMoney,
  taxMode: z.enum(["NONE", "RATE", "AMOUNT"]),
  taxRate: formPercent,
  taxAmount: formMoney,
  taxIncludesShipping: z.boolean(),
  itemCost: formMoney,
  shippingCost: formMoney,
  packagingCost: formMoney,
  otherExpenses: formMoney,
  subscriptionOverhead: formMoney,
  promotedListingPercent: formPercent,
  marketplacePercent: formOptionalPercent,
  marketplaceFixed: formOptionalMoney,
  paymentPercent: formOptionalPercent,
  paymentFixed: formOptionalMoney,
});

export type CalculatorFormValues = z.input<typeof calculatorFormSchema>;
export type CalculatorFormParsed = z.output<typeof calculatorFormSchema>;

/** Maps parsed form values to an engine input. */
export function formToCalculationInput(form: CalculatorFormParsed, currency: string): CalculationInput {
  const salesTax: CalculationInput["salesTax"] =
    form.taxMode === "RATE"
      ? { mode: "RATE", ratePercent: form.taxRate, includeShipping: form.taxIncludesShipping }
      : form.taxMode === "AMOUNT"
        ? { mode: "AMOUNT", amount: form.taxAmount }
        : { mode: "NONE" };

  const override = (percent?: string, fixedAmount?: string) => {
    if (percent == null && fixedAmount == null) return undefined;
    const o: { percent?: string; fixedAmount?: string } = {};
    if (percent != null) o.percent = percent;
    if (fixedAmount != null) o.fixedAmount = fixedAmount;
    return o;
  };

  const marketplace = override(form.marketplacePercent, form.marketplaceFixed);
  const paymentProcessing = override(form.paymentPercent, form.paymentFixed);

  return {
    currency,
    salePrice: form.salePrice,
    shippingCharged: form.shippingCharged,
    salesTax,
    itemCost: form.itemCost,
    shippingCost: form.shippingCost,
    packagingCost: form.packagingCost,
    otherExpenses: form.otherExpenses,
    subscriptionOverhead: form.subscriptionOverhead,
    promotedListingPercent: form.promotedListingPercent,
    overrides: {
      ...(marketplace ? { marketplace } : {}),
      ...(paymentProcessing ? { paymentProcessing } : {}),
    },
  };
}

export { z };
